import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Paper,
  CircularProgress
} from '@mui/material';
import axios from 'axios';
import baseURL from '../../../baseURL';

const formatDate = (d) => {
  if (!d) return '';
  const x = new Date(d);
  return `${String(x.getDate()).padStart(2, '0')}/${String(x.getMonth() + 1).padStart(2, '0')}/${x.getFullYear()}`;
};

const AuctionEntryTab = () => {
  const [ticketNo, setTicketNo] = useState('');
  const [catalogues, setCatalogues] = useState([]);
  const [selectedCatalogue, setSelectedCatalogue] = useState('');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [popupOpen, setPopupOpen] = useState(false);
  const [serialNo, setSerialNo] = useState('');
  const [cashBillNo, setCashBillNo] = useState('');
  const [cashReceiptNo, setCashReceiptNo] = useState('');
  const [bidAmount, setBidAmount] = useState('');
  const [auctionCharges, setAuctionCharges] = useState('');
  const [purchaserName, setPurchaserName] = useState('');
  const [purchaserAddress, setPurchaserAddress] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${baseURL}/auction/auction-dates`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setCatalogues(res.data.data || []))
      .catch(() => setCatalogues([]));
  }, []);

  const catalogue = catalogues.find((c) => c._id === selectedCatalogue);
  const auctionDate = catalogue?.auctionDate;

  const loadSummary = () => {
    if (!ticketNo || !selectedCatalogue || !auctionDate) {
      setError('Enter Pledge Ticket No and select Auction Date (catalogue)');
      return;
    }
    setError('');
    setLoading(true);
    const token = localStorage.getItem('token');
    axios.get(`${baseURL}/auction/entry/summary/${encodeURIComponent(ticketNo)}`, {
      params: { auctionDate: typeof auctionDate === 'string' ? auctionDate : new Date(auctionDate).toISOString().slice(0, 10) },
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => {
        setSummary(res.data.data);
      })
      .catch((err) => {
        setError(err.response?.data?.message || err.message);
        setSummary(null);
      })
      .finally(() => setLoading(false));
  };

  const handleYes = () => {
    setPopupOpen(true);
  };

  const submitPopup = () => {
    if (!summary || !selectedCatalogue) return;
    setError('');
    setLoading(true);
    const token = localStorage.getItem('token');
    axios.post(
      `${baseURL}/auction/entry`,
      {
        catalogueId: selectedCatalogue,
        pledgeId: summary.pledgeId,
        ticketNo: summary.ticketNo,
        serialNo: serialNo || undefined,
        cashBillNo: cashBillNo || undefined,
        cashReceiptNo: cashReceiptNo || undefined,
        bidAmount: Number(bidAmount) || 0,
        auctionCharges: Number(auctionCharges) || 0,
        purchaserNameAddress: [purchaserName, purchaserAddress].filter(Boolean).join('\n') || undefined
      },
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then((res) => {
        setLastResult(res.data.data);
        setPopupOpen(false);
        setConfirmOpen(true);
        setSummary(null);
        setTicketNo('');
        setSerialNo('');
        setCashBillNo('');
        setCashReceiptNo('');
        setBidAmount('');
        setAuctionCharges('');
        setPurchaserName('');
        setPurchaserAddress('');
      })
      .catch((err) => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Auction Entry</Typography>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField label="P.T. No." value={ticketNo} onChange={(e) => setTicketNo(e.target.value)} size="small" sx={{ width: 160 }} />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Auction Date (Catalogue)</InputLabel>
          <Select value={selectedCatalogue} onChange={(e) => setSelectedCatalogue(e.target.value)} label="Auction Date (Catalogue)">
            {catalogues.map((c) => (
              <MenuItem key={c._id} value={c._id}>
                {c.catalogueNo} – {formatDate(c.auctionDate)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={loadSummary} disabled={loading}>Load</Button>
      </Box>

      {summary && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2">P.T. Date: {formatDate(summary.pledgeDate)}</Typography>
            <Typography variant="subtitle2">Auction Date: {formatDate(summary.auctionDate)}</Typography>
            <Typography variant="subtitle2">No. of Days: {summary.noOfDays}</Typography>
            <Typography variant="subtitle2">Interest @ {summary.rateOfInterest}% p.a.</Typography>
            <Table size="small" sx={{ mt: 1, maxWidth: 400 }}>
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Recd</TableCell>
                  <TableCell>Pending</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Principle</TableCell>
                  <TableCell>{summary.principle?.amount}</TableCell>
                  <TableCell>{summary.principle?.recdAmount}</TableCell>
                  <TableCell>{summary.principle?.pendingAmount}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Interest</TableCell>
                  <TableCell>{summary.interest?.amount}</TableCell>
                  <TableCell>{summary.interest?.recdAmount}</TableCell>
                  <TableCell>{summary.interest?.pendingAmount}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Total</strong></TableCell>
                  <TableCell>{summary.total?.amount}</TableCell>
                  <TableCell>{summary.total?.recdAmount}</TableCell>
                  <TableCell>{summary.total?.pendingAmount}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" color="primary" onClick={handleYes} sx={{ mr: 1 }}>Yes</Button>
              <Button variant="outlined" onClick={() => setSummary(null)}>No</Button>
            </Box>
          </Paper>
        </>
      )}

      <Dialog open={popupOpen} onClose={() => !loading && setPopupOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Auction Entry Details</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Serial No (from auction list)" value={serialNo} onChange={(e) => setSerialNo(e.target.value)} size="small" />
            <TextField label="Cash Bill No" value={cashBillNo} onChange={(e) => setCashBillNo(e.target.value)} size="small" />
            <TextField label="Cash Receipt No" value={cashReceiptNo} onChange={(e) => setCashReceiptNo(e.target.value)} size="small" />
            <TextField label="Bid Amount" type="number" value={bidAmount} onChange={(e) => setBidAmount(e.target.value)} size="small" />
            <TextField label="Auction Charges" type="number" value={auctionCharges} onChange={(e) => setAuctionCharges(e.target.value)} size="small" />
            <TextField label="Name of Purchaser" value={purchaserName} onChange={(e) => setPurchaserName(e.target.value)} size="small" fullWidth />
            <TextField label="Address of Purchaser" value={purchaserAddress} onChange={(e) => setPurchaserAddress(e.target.value)} size="small" multiline rows={2} fullWidth />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPopupOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitPopup} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Confirm Entry'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Entry OK</DialogTitle>
        <DialogContent>
          {lastResult?.message && <Typography color="primary">{lastResult.message}</Typography>}
          <Typography variant="body2">Auction entry saved.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuctionEntryTab;
